/**
 * CompletedStage Component
 *
 * In-page completion view for the new learning page.
 * Displays completion summary with score/statistics and navigation options.
 * Works for both video and quiz completion.
 */

import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import {
    CheckCircle2,
    RotateCcw,
    ChevronRight,
    List,
    ArrowLeft,
    Clock,
    Target,
    Award,
    Play,
} from 'lucide-react'
import type { CompleteSessionResponse } from '@/api/learningSession'

export interface CompletedStageProps {
    /** Lesson title for display */
    lessonTitle: string
    /** Type of completed component */
    componentType: 'video' | 'quiz' | 'assignment'
    /** Completion data from API */
    completionData: CompleteSessionResponse
    /** Whether there is a next lesson available */
    hasNextLesson: boolean
    /** Handler to restart the current component */
    onRestart: () => void
    /** Handler to navigate to next lesson */
    onNextLesson?: () => void
    /** Handler to go back to lesson overview */
    onBackToOverview: () => void
    /** Handler to navigate to course list */
    onNavigateToList: () => void
    /** Optional className */
    className?: string
}

/**
 * Format seconds to a human-readable time string (e.g., "5분 30초")
 */
function formatDuration(seconds: number, t: (key: string, opts?: Record<string, unknown>) => string): string {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60

    if (mins === 0) {
        return t('learning.completed.seconds', { secs })
    }
    if (secs === 0) {
        return t('learning.completed.minutes', { mins })
    }
    return t('learning.completed.minutesSeconds', { mins, secs })
}

export function CompletedStage({
    lessonTitle,
    componentType,
    completionData,
    hasNextLesson,
    onRestart,
    onNextLesson,
    onBackToOverview,
    onNavigateToList,
    className,
}: CompletedStageProps) {
    const { t } = useTranslation('learner')
    const isQuizOrAssignment = componentType === 'quiz' || componentType === 'assignment'
    const isPassed = isQuizOrAssignment ? completionData.passed : true

    return (
        <div className={cn(
            'max-w-2xl mx-auto py-8 px-4 animate-in fade-in zoom-in-95 duration-300',
            className
        )}>
            {/* Success Icon */}
            <div className="flex justify-center mb-8">
                <div className={cn(
                    'w-24 h-24 rounded-full flex items-center justify-center shadow-lg',
                    isPassed
                        ? 'bg-gradient-to-br from-green-400 to-green-600'
                        : 'bg-gradient-to-br from-amber-400 to-amber-600'
                )}>
                    {isPassed ? (
                        <CheckCircle2 className="w-14 h-14 text-white" />
                    ) : (
                        <Target className="w-14 h-14 text-white" />
                    )}
                </div>
            </div>

            {/* Completion Message */}
            <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-3">
                    {isQuizOrAssignment
                        ? (isPassed ? t('learning.completed.quizPassed') : t('learning.completed.quizCompleted'))
                        : t('learning.completed.videoCompleted')
                    }
                </h1>
                <p className="text-lg text-gray-600">
                    <span className="font-semibold text-gray-900">{lessonTitle}</span>
                    <br />
                    {isQuizOrAssignment
                        ? (isPassed ? t('learning.completed.congratsQuizPassed') : t('learning.completed.quizDone'))
                        : t('learning.completed.videoDone')
                    }
                </p>
            </div>

            {/* Statistics Card */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-8">
                <div className="p-6">
                    <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Award className="w-5 h-5 text-primary-600" />
                        {t('learning.completed.learningResult')}
                    </h2>

                    {isQuizOrAssignment ? (
                        <QuizStats completionData={completionData} t={t} />
                    ) : (
                        <VideoStats completionData={completionData} t={t} />
                    )}
                </div>
            </div>

            {/* Navigation Buttons */}
            <div className="space-y-4">
                {/* Primary Actions Row */}
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                        onClick={onRestart}
                        className="flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-300 font-semibold transition-all"
                    >
                        <RotateCcw className="w-5 h-5" />
                        <span>{isQuizOrAssignment ? t('learning.completed.retakeQuiz') : t('learning.completed.rewatchVideo')}</span>
                    </button>

                    {hasNextLesson && onNextLesson ? (
                        <button
                            onClick={onNextLesson}
                            className="flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 font-semibold transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                        >
                            <span>{t('learning.completed.nextLesson')}</span>
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    ) : (
                        <button
                            onClick={onBackToOverview}
                            className="flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 font-semibold transition-all shadow-lg"
                        >
                            <Play className="w-5 h-5" />
                            <span>{t('learning.completed.toOverview')}</span>
                        </button>
                    )}
                </div>

                {/* Secondary Actions Row */}
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    {hasNextLesson && onNextLesson && (
                        <button
                            onClick={onBackToOverview}
                            className="flex items-center justify-center gap-2 px-5 py-2.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg font-medium transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            <span>{t('learning.completed.toOverview')}</span>
                        </button>
                    )}

                    <button
                        onClick={onNavigateToList}
                        className="flex items-center justify-center gap-2 px-5 py-2.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg font-medium transition-colors"
                    >
                        <List className="w-4 h-4" />
                        <span>{t('learning.completed.toCourseList')}</span>
                    </button>
                </div>
            </div>
        </div>
    )
}

/**
 * Video completion statistics
 */
interface VideoStatsProps {
    completionData: CompleteSessionResponse
    t: (key: string, opts?: Record<string, unknown>) => string
}

function VideoStats({ completionData, t }: VideoStatsProps) {
    const { totalWatchTimeSec, progressPercent } = completionData

    return (
        <div className="grid grid-cols-2 gap-4">
            {/* Watch Time */}
            <div className="bg-blue-50 rounded-xl p-4 text-center">
                <div className="flex justify-center mb-2">
                    <Clock className="w-8 h-8 text-blue-600" />
                </div>
                <p className="text-sm text-blue-600 font-medium mb-1">{t('learning.completed.totalWatchTime')}</p>
                <p className="text-2xl font-bold text-blue-900">
                    {totalWatchTimeSec != null ? formatDuration(totalWatchTimeSec, t) : '-'}
                </p>
            </div>

            {/* Progress */}
            <div className="bg-green-50 rounded-xl p-4 text-center">
                <div className="flex justify-center mb-2">
                    <Target className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-sm text-green-600 font-medium mb-1">{t('learning.completed.progressRate')}</p>
                <p className="text-2xl font-bold text-green-900">
                    {progressPercent != null ? `${progressPercent}%` : '-'}
                </p>
            </div>
        </div>
    )
}

/**
 * Quiz completion statistics
 */
interface QuizStatsProps {
    completionData: CompleteSessionResponse
    t: (key: string, opts?: Record<string, unknown>) => string
}

function QuizStats({ completionData, t }: QuizStatsProps) {
    const { score, passed, correctCount, totalQuestions } = completionData

    return (
        <div className="space-y-4">
            {/* Score & Pass Status */}
            <div className="grid grid-cols-2 gap-4">
                {/* Score */}
                <div className={cn(
                    'rounded-xl p-4 text-center',
                    passed ? 'bg-green-50' : 'bg-amber-50'
                )}>
                    <div className="flex justify-center mb-2">
                        <Award className={cn(
                            'w-8 h-8',
                            passed ? 'text-green-600' : 'text-amber-600'
                        )} />
                    </div>
                    <p className={cn(
                        'text-sm font-medium mb-1',
                        passed ? 'text-green-600' : 'text-amber-600'
                    )}>
                        {t('learning.completed.score')}
                    </p>
                    <p className={cn(
                        'text-3xl font-bold',
                        passed ? 'text-green-900' : 'text-amber-900'
                    )}>
                        {score != null ? t('learning.completed.scoreValue', { score }) : '-'}
                    </p>
                </div>

                {/* Pass Status */}
                <div className={cn(
                    'rounded-xl p-4 text-center',
                    passed ? 'bg-green-50' : 'bg-red-50'
                )}>
                    <div className="flex justify-center mb-2">
                        {passed ? (
                            <CheckCircle2 className="w-8 h-8 text-green-600" />
                        ) : (
                            <Target className="w-8 h-8 text-red-600" />
                        )}
                    </div>
                    <p className={cn(
                        'text-sm font-medium mb-1',
                        passed ? 'text-green-600' : 'text-red-600'
                    )}>
                        {t('learning.completed.result')}
                    </p>
                    <p className={cn(
                        'text-2xl font-bold',
                        passed ? 'text-green-900' : 'text-red-900'
                    )}>
                        {passed ? t('learning.completed.passed') : t('learning.completed.failed')}
                    </p>
                </div>
            </div>

            {/* Correct/Total */}
            {correctCount != null && totalQuestions != null && (
                <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                        <span className="text-gray-600 font-medium">{t('learning.completed.correctRate')}</span>
                        <div className="flex items-center gap-2">
                            <span className="text-xl font-bold text-gray-900">
                                {correctCount} / {totalQuestions}
                            </span>
                            <span className="text-sm text-gray-500">
                                ({Math.round((correctCount / totalQuestions) * 100)}%)
                            </span>
                        </div>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                            className={cn(
                                'h-full rounded-full transition-all duration-500',
                                passed ? 'bg-green-500' : 'bg-amber-500'
                            )}
                            style={{ width: `${(correctCount / totalQuestions) * 100}%` }}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}
